import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/card';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import { computeNodeStats } from '../../utils/tenantMapModel';
import { TENANT_NODE_TABS } from '../../config/tenantNodeTabsConfig';
import TenantProfileForm from './TenantProfileForm';

function TenantOverviewPanel({
  profile,
  saving,
  loading,
  nodesByKey,
  supplierCount = 0,
  onSaveProfile,
  onOpenDataSection,
}) {
  const { t } = useLocale();
  const stats = useMemo(() => computeNodeStats(nodesByKey), [nodesByKey]);

  const summaryItems = TENANT_NODE_TABS.map((tab) => ({
    section: tab.pageTab,
    label: t(tab.labelKey),
    count: stats[tab.key] ?? 0,
  }));

  return (
    <div className="flex flex-col gap-6">
      <Card className={themeClasses.appKit.sectionShell}>
        <CardHeader>
          <CardTitle>{t('tenantProfileTitle')}</CardTitle>
          <CardDescription>{t('tenantProfileDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <TenantProfileForm
            profile={profile}
            saving={saving || loading}
            onSave={onSaveProfile}
            embedded
          />
        </CardContent>
      </Card>
      <Card className={themeClasses.appKit.sectionShell}>
        <CardHeader>
          <CardTitle>{t('tenantOverviewSummaryTitle')}</CardTitle>
          <CardDescription>{t('tenantOverviewSummaryDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border-light">
            <li className="flex items-center justify-between py-3 text-sm first:pt-0">
              <span className="text-text-primary">{t('tenantStatSuppliers')}</span>
              <span className="font-semibold tabular-nums text-text-secondary">{supplierCount}</span>
            </li>
            {summaryItems.map((item) => (
              <li key={item.section} className="last:pb-0">
                <button
                  type="button"
                  onClick={() => onOpenDataSection?.(item.section)}
                  className="flex w-full items-center justify-between gap-2 py-3 text-sm text-left transition-colors hover:text-primary-600"
                >
                  <span className="text-text-primary">{item.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-semibold tabular-nums text-text-secondary">
                      {item.count}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
                    <span className="sr-only">
                      {t('tenantOverviewSummaryOpen', { section: item.label })}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default TenantOverviewPanel;
